/**
 * Contact Migration Rollback card — lives in /admin/diagnostics.
 *
 * Lists every canonical-migration batch with its rollback handle. Operator
 * picks a batch, previews the per-record diff that a restore would cause,
 * then commits by TYPING the batch_id into a confirm field (no buttons-only
 * fire — typed confirmation prevents fat-finger restores).
 *
 * Restore itself is reversible — backend writes an inverse_batch_id snapshot
 * BEFORE mutating db.contacts so a restore can be un-restored.
 *
 * Super-admin only — backend 403s non-super-admins; UI hides itself on the
 * 403 so non-admins don't see a dangerous-looking card they can't use.
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Database, Loader2, AlertTriangle, RotateCcw, Eye, CheckCircle2,
  ChevronDown, ChevronRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ContactMigrationRollbackCard() {
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [previewByBatch, setPreviewByBatch] = useState({});
  const [previewLoading, setPreviewLoading] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [confirmInput, setConfirmInput] = useState({});

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/admin/contact-migration/batches`,
        { headers: authHeaders() },
      );
      setBatches(res.data?.batches || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setHidden(true);
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to load migration batches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (expanded) fetchBatches();
  }, [expanded, fetchBatches]);

  const loadPreview = async (batchId) => {
    setPreviewLoading(batchId);
    try {
      const res = await axios.get(
        `${API}/api/admin/contact-migration/batches/${encodeURIComponent(batchId)}/preview`,
        { headers: authHeaders() },
      );
      setPreviewByBatch(prev => ({ ...prev, [batchId]: res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(null);
    }
  };

  const executeRestore = async (batchId) => {
    const typed = (confirmInput[batchId] || '').trim();
    if (typed !== batchId) {
      toast.error('Type the exact batch_id to confirm the restore');
      return;
    }
    setRestoring(batchId);
    try {
      const res = await axios.post(
        `${API}/api/admin/contact-migration/batches/${encodeURIComponent(batchId)}/restore`,
        { confirm_batch_id: batchId },
        { headers: authHeaders() },
      );
      toast.success(
        `Restored ${res.data.applied + res.data.reinserted}/${res.data.total_records} records. Inverse backup: ${res.data.inverse_batch_id.slice(0, 60)}…`,
      );
      setConfirmInput(prev => ({ ...prev, [batchId]: '' }));
      await fetchBatches();
      // Re-preview so the card reflects post-restore state
      await loadPreview(batchId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  if (hidden) return null;

  return (
    <Card className="border-rose-200" data-testid="contact-migration-rollback-card">
      <CardHeader
        className="cursor-pointer flex flex-row items-start justify-between gap-4"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-3">
          {expanded ? <ChevronDown className="h-5 w-5 mt-0.5" /> : <ChevronRight className="h-5 w-5 mt-0.5" />}
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4" />
              Contact Migration Rollback
              <Badge className="bg-rose-100 text-rose-900 border-rose-300 border ml-1">
                destructive
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              List canonical-migration batches and restore any of them. Typed-confirmation gated. Restore is itself reversible.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          {loading && (
            <p className="text-sm text-stone-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          )}
          {!loading && batches.length === 0 && (
            <p className="text-sm text-stone-500" data-testid="no-batches-msg">
              No canonical-migration batches found.
            </p>
          )}
          {batches.map(b => {
            const preview = previewByBatch[b.batch_id];
            const typed = (confirmInput[b.batch_id] || '').trim();
            const canRestore = typed === b.batch_id;
            return (
              <div
                key={b.batch_id}
                className="rounded-md border border-stone-200 bg-stone-50 p-3 space-y-2"
                data-testid={`batch-row-${b.batch_id.slice(0, 24)}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm space-y-0.5">
                    <p className="font-mono text-xs text-stone-700 break-all">{b.batch_id}</p>
                    <p className="text-stone-600">
                      {b.record_count} record{b.record_count === 1 ? '' : 's'} ·
                      tenant{b.company_ids.length > 1 ? 's' : ''}: {b.company_ids.map(c => c.slice(0, 8)).join(', ')} ·
                      captured {new Date(b.first_captured_at).toLocaleString()}
                    </p>
                    {b.fully_restored && (
                      <p className="text-emerald-700 text-xs flex items-center gap-1 mt-1">
                        <CheckCircle2 className="h-3 w-3" /> Previously restored — re-restore will write a fresh inverse backup
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => loadPreview(b.batch_id)}
                    disabled={previewLoading === b.batch_id}
                    data-testid={`preview-restore-btn-${b.batch_id.slice(0, 24)}`}
                  >
                    {previewLoading === b.batch_id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    ) : (
                      <Eye className="h-3.5 w-3.5 mr-1" />
                    )}
                    Preview restore
                  </Button>
                </div>
                {preview && (
                  <div className="text-xs bg-white border border-stone-200 rounded p-2 space-y-1">
                    <p className="font-semibold text-stone-700">
                      Preview — would change {preview.would_change} ·
                      noop {preview.would_noop} ·
                      reinsert {preview.would_reinsert} ·
                      total {preview.total_records}
                    </p>
                    <div className="max-h-44 overflow-y-auto divide-y divide-stone-100">
                      {preview.diffs.map(d => (
                        <div
                          key={d.contact_id}
                          className="py-1.5"
                          data-testid={`preview-diff-row-${d.contact_id.slice(0, 8)}`}
                        >
                          <div className="flex items-center gap-2">
                            <Badge
                              className={
                                d.action === 'restore'
                                  ? 'bg-amber-100 text-amber-900 border-amber-300 border'
                                  : d.action === 'reinsert'
                                  ? 'bg-rose-100 text-rose-900 border-rose-300 border'
                                  : 'bg-stone-100 text-stone-600 border-stone-300 border'
                              }
                            >
                              {d.action}
                            </Badge>
                            <span className="text-stone-700">{d.name_hint}</span>
                          </div>
                          {(d.added.length || d.removed.length || d.modified.length) ? (
                            <p className="text-stone-500 text-xs ml-1 mt-0.5">
                              {d.added.length ? `+${d.added.join(', ')}` : ''}
                              {d.added.length && (d.removed.length || d.modified.length) ? ' · ' : ''}
                              {d.removed.length ? `−${d.removed.join(', ')}` : ''}
                              {d.removed.length && d.modified.length ? ' · ' : ''}
                              {d.modified.length ? `~${d.modified.join(', ')}` : ''}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                    {preview.would_change + preview.would_reinsert > 0 && (
                      <div className="mt-2 pt-2 border-t border-stone-200 space-y-2">
                        <p className="text-amber-900 text-xs flex items-start gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
                          To restore, type the batch_id exactly to confirm:
                        </p>
                        <Input
                          value={typed}
                          onChange={(e) => setConfirmInput(prev => ({ ...prev, [b.batch_id]: e.target.value }))}
                          placeholder="paste / type the batch_id"
                          className="font-mono text-xs"
                          data-testid={`confirm-input-${b.batch_id.slice(0, 24)}`}
                        />
                        <Button
                          size="sm"
                          disabled={!canRestore || restoring === b.batch_id}
                          onClick={() => executeRestore(b.batch_id)}
                          className="bg-rose-600 hover:bg-rose-700 text-white"
                          data-testid={`execute-restore-btn-${b.batch_id.slice(0, 24)}`}
                        >
                          {restoring === b.batch_id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                          ) : (
                            <RotateCcw className="h-3.5 w-3.5 mr-1" />
                          )}
                          Restore this batch
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      )}
    </Card>
  );
}
